import React, { Fragment, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { DatePicker, Upload } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { PlusCircleFilled, PlusOutlined } from '@ant-design/icons';
import Constants, { DEFAULT_CUR_ROUND_OFF, toISTDate } from '@Apis/constants';
import { SERVICE_ENDPOINT } from '../../../../apis/constants';

// Components
import H3Text from '@Uilib/h3Text';
import ErrorHandle from '../../../Common/ErrorHandle';
import RestrictedAccessMessage from '../../../Common/RestrictedAccess/RestrictedAccessMessage';
import PRZButton from '../../../Common/UI/PRZButton';
import PRZConfirmationPopover from '../../../Common/UI/PRZConfirmationPopover';
import PRZInput from '../../../Common/UI/PRZInput';
import PRZText from '../../../Common/UI/PRZText';
import ViewLoadingSkull from '../../../Common/ViewLoadingSkull';
import SelectDepartment from '@Components/Common/SelectDepartment';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import CustomDocumentInputs from '@Components/Common/CustomDocumentInputs';
import CustomFieldV3 from '@Components/Common/CustomFieldV3';
import TagSelector from '@Components/Common/Selector/TagSelector';
import DocumentNumberSeqInput from '../../../Admin/Common/DocumentNumberSeqInput';

// Helpers
import Helpers from '@Apis/helpers';
import FormHelpers from '@Helpers/FormHelpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';

// Actions
import CFV2Actions from '@Actions/configurations/cfV2Actions';
import TagActions from '@Actions/tagActions';
import OfferActions from '@Actions/offerActions';

// Current File Imports
import { CreatePurchaseIndent, UpdatePurchaseIndent, GetPurchaseIndent } from '../../../../modules/purchase/purchaseIndent';
import purchaseIndentErrors from './purchaseIndentErrors';
import PurchaseIndentLines from './PurchaseIndentLines';
import './style.scss';

const uploadButton = (
  <div>
    <PlusOutlined />
    <div style={{ marginTop: 8 }}>Upload</div>
  </div>
);

function PurchaseIndentForm({
  user,
  cfV2DocPurchaseIndent,
  priceMasking,
  createPILoading,
  updatePILoading,
  getPILoading,
  createPurchaseIndent,
  updatePurchaseIndent,
  getPurchaseIndent,
  getDocCFV2,
  getTenantSkuOffer,
  createTag,
  match,
  history,
  selectedPR,
  selectedMO,
  selectedMRP,
  mrpToPiData,
  selectedPurchaseIndent,
  getPurchaseIndentSuccess
}) {

  const [state, setState] = useState({
    piId: null,
    formSubmitted: false,
    data: [
      {
        key: uuidv4(),
        asset1: '',
        product_sku_name: '',
        quantity: '',
        uom_id: null,
        req_by_date: undefined,
        plan_number: '',
        lineCustomFields: [],
      },
    ],
    updateDocumentReason: '',
    cfPurchaseIndentDoc: [],
    cfPurchaseIndentLine: [],
    visibleColumns: {
      PRODUCT: {
        label: 'Product',
        visible: true,
        disabled: true,
      },
      QUANTITY: {
        label: 'Quantity',
        visible: true,
        disabled: true,
      },
      UNIT: {
        label: 'Unit',
        visible: true,
        disabled: true,
      },
      REQ_BY_DATE: {
        label: 'Required By Date',
        visible: true,
        disabled: true,
      },
      PLAN_NUMBER: {
        label: 'Planning Number',
        visible: true,
        disabled: true,
      },
    },
    piNumber: '',
    initialPiNumber: '',
    docSeqId: null,
    selectedTenant: null,
    selectedDepartment: null,
    selectedTenantDepartment: null,
    date: dayjs(),
    selectedTags: [],
    remark: '',
    fileList: [],
    currentAction: false,
    selectedPurchaseIndent: null,
    formCase: 'CREATE', // can be 'CREATE', 'UPDATE' or 'CLONE'
    prId: null,
    moId: null,
    mrpId: null,
  });
  const {
    piId,
    formSubmitted,
    data,
    updateDocumentReason,
    cfPurchaseIndentDoc,
    cfPurchaseIndentLine,
    visibleColumns,
    piNumber,
    initialPiNumber,
    docSeqId,
    selectedTenant,
    selectedDepartment,
    selectedTenantDepartment,
    date,
    selectedTags,
    remark,
    fileList,
    currentAction,
    formCase,
    prId,
    moId,
    mrpId,
  } = state;
  const updateState = (newStates) => {
    setState((oldStates) => ({
      ...oldStates,
      ...newStates,
    }));
  };

  const getTenantDepartmentInfo = (tenantId) => {
    return user?.user_tenants?.find((i) => i.tenant_id === tenantId)?.tenant_department_info;
  };

  useEffect(() => {
    if (!!user) {
      updateState({
        selectedTenant: user?.tenant_info?.tenant_id,
        selectedDepartment: Helpers.getDepartmentId(user),
        selectedTenantDepartment: Helpers.getTenantDepartmentId(user),
      });
      getDocCFV2({
        orgId: user?.tenant_info?.org_id,
        entityName: 'PURCHASE_INDENT',
      });
    }
  }, [user]);

  useEffect(() => {
    if (!!match && !!location) {
      // GET PURCHASE INDENT FOR UPDATE OR CLONE CASE
      const urlParams = new URLSearchParams(window?.location?.search);
      const updatePiId = match?.params?.piId;
      const clonePiId = location?.state?.clonePiId || urlParams.get('clonePiId');

      updateState({ piId: updatePiId || clonePiId });
      if (updatePiId || clonePiId) {
        getPurchaseIndent({
          url: SERVICE_ENDPOINT.GET_PURCHASE_INDENT({ pi_id: updatePiId || clonePiId }).url,
        });
        updateState({
          formCase: !!updatePiId ? 'UPDATE' : !!clonePiId ? 'CLONE' : 'CREATE',
        });
      }
    }

    return () => {
      getPurchaseIndentSuccess(null);
    }
  }, []);

  useEffect(() => {
    if (cfV2DocPurchaseIndent?.data?.success) {
      // ADHOC PURCHASE INDENT
      {
        const lineCFs = CustomFieldHelpers.getCfStructure(cfV2DocPurchaseIndent?.data?.document_line_custom_fields?.filter((item) => (item?.is_active)), false);
        updateState({
          cfPurchaseIndentDoc: CustomFieldHelpers.getCfStructure(cfV2DocPurchaseIndent?.data?.document_custom_fields, []) || [],
          cfPurchaseIndentLine: lineCFs,
          visibleColumns: CustomFieldHelpers.updateVisibleColumns(cfV2DocPurchaseIndent?.data?.document_line_custom_fields, visibleColumns),
          data: data?.map((item) => ({
            ...item,
            lineCustomFields: lineCFs,
          })),
        })
      }

      // UPDATE OR CLONE PURCHASE INDENT
      if (!!selectedPurchaseIndent) {
        const oldCustomField = selectedPurchaseIndent?.custom_fields || [];
        const oldLineCustomField = selectedPurchaseIndent?.purchase_indent_lines?.[0]?.custom_fields || [];
        const piNumber = formCase === 'UPDATE' ? selectedPurchaseIndent?.pi_number : '';
        updateState({
          piNumber,
          selectedTenant: selectedPurchaseIndent?.tenant_id,
          selectedDepartment: selectedPurchaseIndent?.department_id,
          selectedTenantDepartment: selectedPurchaseIndent?.tenant_department_id,
          selectedTags: selectedPurchaseIndent?.tags || [],
          cfPurchaseIndentDoc: CustomFieldHelpers?.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_custom_fields || [], oldCustomField) || [],
          cfPurchaseIndentLine: CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldLineCustomField) || [],
          visibleColumns: CustomFieldHelpers.updateVisibleColumns(CustomFieldHelpers.postCfStructure(CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldLineCustomField)), visibleColumns),
          remark: selectedPurchaseIndent?.remarks,
          fileList: selectedPurchaseIndent?.attachments,
          data: selectedPurchaseIndent?.purchase_indent_lines?.map((line) => {
            const oldCf = line?.custom_fields || [];
            return {
              key: uuidv4(),
              tenant_product_id: line?.tenant_product_id,
              pi_line_id: line?.pi_line_id,
              asset1: line?.product_sku_info?.assets?.[0]?.url || '',
              product_sku_name: line?.product_sku_name,
              product_sku_id: line?.product_sku_info?.product_sku_id,
              product_sku_info: line?.product_sku_info,
              ref_product_code: line?.product_sku_info?.ref_product_code,
              internal_sku_code: line?.product_sku_info?.internal_sku_code,
              remark: line?.remarks,
              remarkRequired: !!line?.remarks?.trim()?.length,
              quantity: line?.quantity,
              uom_id: line?.uom_info?.[0]?.uom_id || line?.uom_id,
              uomId: line?.uom_info?.[0]?.uom_id || line?.uom_id,
              uom_list: line?.product_sku_info?.uom_list,
              uqc: line?.uqc || '',
              uomGroup: line?.group_id,
              uom_info: line?.uom_info?.[0],
              req_by_date: toISTDate(line?.required_by_date, 'DD/MM/YYYY'),
              plan_number: line?.planning_number,
              lineCustomFields: CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldCf || []) || [],
            };
          })
        });
      }

      // PURCHASE INDENT FROM PURCHASE REQUEST
      if (!!selectedPR) {
        const oldCustomField = selectedPR?.custom_fields || [];
        const oldLineCustomField = selectedPR?.purchase_requisition_lines?.[0]?.custom_fields || [];
        updateState({
          prId: selectedPR?.purchase_requisition_id,
          selectedTenant: selectedPR?.tenant_id,
          selectedDepartment: selectedPR?.department_id,
          selectedTenantDepartment: selectedPR?.tenant_department_id,
          selectedTags: selectedPR?.tags || [],
          cfPurchaseIndentDoc: CustomFieldHelpers?.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_custom_fields || [], oldCustomField) || [],
          cfPurchaseIndentLine: CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldLineCustomField) || [],
          visibleColumns: CustomFieldHelpers.updateVisibleColumns(CustomFieldHelpers.postCfStructure(CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldLineCustomField)), visibleColumns),
          remark: selectedPurchaseIndent?.remark,
          fileList: selectedPurchaseIndent?.attachments,
          data: selectedPR?.purchase_requistion_lines?.map((line) => {
            const oldCf = line?.custom_fields || [];
            return {
              key: uuidv4(),
              tenant_product_id: line?.tenant_product_id,
              asset1: line?.product_sku_info?.assets?.[0]?.url || '',
              product_sku_name: line?.product_sku_name,
              product_sku_id: line?.product_sku_info?.product_sku_id,
              product_sku_info: line?.product_sku_info,
              ref_product_code: line?.product_sku_info?.ref_product_code,
              internal_sku_code: line?.product_sku_info?.internal_sku_code,
              remark: line?.remark,
              remarkRequired: !!line?.remark?.trim()?.length,
              quantity: line?.quantity,
              uom_id: line?.uom_info?.uom_id || line?.uom_id,
              uomId: line?.uom_info?.uom_id || line?.uom_id,
              uom_list: line?.product_sku_info?.uom_list,
              uqc: line?.uqc || '',
              uomGroup: line?.group_id,
              uom_info: line?.uom_info,
              req_by_date: toISTDate(new Date(), 'DD/MM/YYYY'),
              lineCustomFields: CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldCf || []) || [],
              pr_line_id: line?.pr_line_id,
            };
          })
        });
      }

      // PURCHASE INDENT FROM MANUFACTURING ORDER
      if (!!selectedMO) {
        const oldCustomField = selectedMO?.custom_fields || [];
        const oldLineCustomField = selectedMO?.mo_lines?.[0]?.custom_fields || [];
        updateState({
          moId: selectedMO?.mo_id,
          selectedTenant: selectedMO?.tenant_id,
          selectedDepartment: selectedMO?.rm_tenant_department_info?.department_id,
          selectedTenantDepartment: selectedMO?.rm_tenant_department_id,
          cfPurchaseIndentDoc: CustomFieldHelpers?.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_custom_fields || [], oldCustomField) || [],
          cfPurchaseIndentLine: CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldLineCustomField) || [],
          visibleColumns: CustomFieldHelpers.updateVisibleColumns(CustomFieldHelpers.postCfStructure(CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldLineCustomField)), {
            ...visibleColumns, FG_SFG: {
              label: 'FG/SFG',
              visible: true,
              disabled: true,
            },
          }),
          remark: selectedMO?.remarks,
          fileList: selectedMO?.attachments,
          data: selectedMO?.mo_lines?.map((line) => {

            const oldCf = line?.custom_fields || [];
            return {
              key: uuidv4(),
              tenant_product_id: line?.tenant_product_id,
              asset1: line?.tenant_product_info?.assets?.[0]?.url || '',
              product_sku_name: line?.tenant_product_info?.product_sku_name,
              product_sku_id: line?.product_sku_id,
              product_sku_info: line?.tenant_product_info,
              ref_product_code: line?.tenant_product_info?.ref_product_code,
              internal_sku_code: line?.tenant_product_info?.internal_sku_code,
              remark: line?.remarks,
              remarkRequired: !!line?.remarks?.trim()?.length,
              quantity: line?.quantity,
              uom_id: line?.uom_info?.uom_id || line?.uom_id,
              uomId: line?.uom_info?.uom_id || line?.uom_id,
              uom_list: line?.tenant_product_info?.uom_list,
              uqc: line?.uqc || '',
              uomGroup: line?.group_id,
              uom_info: line?.uom_info,
              req_by_date: toISTDate(new Date()),
              lineCustomFields: CustomFieldHelpers.mergeCustomFields(cfV2DocPurchaseIndent?.data?.document_line_custom_fields || [], oldCf || []) || [],
              mo_line_id: line?.mo_line_id,
              parent_mo_line_id: line?.parent_mo_line_id,
              fg_tenant_product_id: line?.fg_tenant_product_id,
            };
          })
        });
      }

      // PURCHASE INDENT FROM MATERIALS REQUIREMENT PLANNING
      if (!!selectedMRP) {

        const lineCFs = CustomFieldHelpers.getCfStructure(cfV2DocPurchaseIndent?.data?.document_line_custom_fields?.filter((item) => item?.is_active), false);
        const isPRFromMrpInPurchaseUomEnable = user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.settings?.create_pr_from_mrp_in_purchase_uom;
        const autoPrintDescription = user?.tenant_info?.global_config?.settings?.print_description_automatically;

        updateState({
          mrpId: selectedMRP?.mrp_id,
          selectedTenant: selectedMRP?.tenant_id,
          selectedDepartment: getTenantDepartmentInfo(selectedMRP?.tenant_id)?.department_id,
          selectedTenantDepartment: getTenantDepartmentInfo(selectedMRP?.tenant_id)?.tenant_department_id,
          cfPurchaseIndentDoc: CustomFieldHelpers.getCfStructure(cfV2DocPurchaseIndent?.data?.document_custom_fields, []) || [],
          cfPurchaseIndentLine: lineCFs,
          visibleColumns: CustomFieldHelpers.updateVisibleColumns(cfV2DocPurchaseIndent?.data?.document_line_custom_fields, visibleColumns),
          data: mrpToPiData?.map((line) => {

            const lineCfData = FormHelpers.lineSystemFieldValue(
              lineCFs,
              [
                ...(line?.custom_fields ?? []),
                ...(line?.system_custom_fields ?? []),
              ],
            )?.map((i) => {
              let fieldValue;

              // Handle 'Rate' field
              if (i?.fieldName === 'Rate') {
                fieldValue = Number(0); // Set to 0 if the field is 'Rate'
              }
              // Handle 'Quantity' field with conditional logic based on `isPRFromMrpInPurchaseUomEnable`
              else if (i?.fieldName === 'Quantity') {
                if (isPRFromMrpInPurchaseUomEnable) {
                  fieldValue = Number(
                    (line?.pending_quantity || 0)
                    * (line?.uom_info?.[0]?.ratio || 1)
                    / (line?.product_sku_info?.purchase_uom_info?.ratio || 1),
                  );
                } else {
                  fieldValue = line?.pending_quantity;
                }
              }
              // Default case for other fields
              else {
                fieldValue = i?.fieldValue;
              }

              // Return the modified object
              return {
                ...i,
                fieldValue,
              };
            });
            return {
              key: uuidv4(),
              tenant_product_id: line?.tenant_product_id,
              asset1: line?.product_sku_info?.assets?.[0]?.url || '',
              product_sku_name: line?.product_sku_name,
              product_sku_id: line?.product_sku_info?.product_sku_id,
              product_sku_info: line?.product_sku_info,
              ref_product_code: line?.ref_product_code,
              internal_sku_code: line?.internal_sku_code,
              remark: autoPrintDescription ? line?.product_sku_info?.description?.replace(/<[^>]+>/g, '') : '',
              remarkRequired: autoPrintDescription ? !!line?.product_sku_info?.description?.replace(/<[^>]+>/g, '') : false,
              remarkRequired: !!line?.remark?.trim()?.length,
              quantity: isPRFromMrpInPurchaseUomEnable ? (
                (line?.pending_quantity || 0) * (line?.uom_info?.[0]?.ratio || 1)) / (line?.product_sku_info?.purchase_uom_info?.ratio || 1
                ) : line?.pending_quantity,
              uom_id: isPRFromMrpInPurchaseUomEnable ? line?.product_sku_info?.purchase_uom_info?.uom_id : line?.uom_info?.[0]?.uom_id || '',
              uomId: isPRFromMrpInPurchaseUomEnable ? line?.product_sku_info?.purchase_uom_info?.uom_id : line?.uom_info?.[0]?.uom_id || '',
              uom_list: line?.product_uom_list,
              uom_info: isPRFromMrpInPurchaseUomEnable ? line?.product_sku_info?.purchase_uom_info : line?.uom_info?.[0],
              uqc: isPRFromMrpInPurchaseUomEnable ? line?.product_sku_info?.purchase_uom_info?.uqc : line?.product_sku_info?.uqc || '',
              uomGroup: line?.group_id,
              req_by_date: toISTDate(new Date(), 'DD/MM/YYYY'),
              lineCustomFields: lineCfData,
            };
          })
        });
      }
    }
  }, [cfV2DocPurchaseIndent, selectedPurchaseIndent, selectedPR, selectedMO]);

  const customInputChange = (fieldValue, cfId) => {
    const newCustomField = cfPurchaseIndentDoc.map((customField) => {
      if (customField?.cfId === cfId) {
        if (customField?.fieldType === 'ATTACHMENT') {
          return {
            ...customField,
            fieldValue: fieldValue?.map((attachment) => ({
              url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
            })),

          };
        }
        return {
          ...customField,
          fieldValue,
        };
      }
      return {
        ...customField,
      };
    });
    updateState({
      cfPurchaseIndentDoc: newCustomField,
    });
  };

  const handleProductChangeValue = (value, key) => {

    const copyData = JSON.parse(JSON.stringify(data));
    for (let i = 0; i < copyData.length; i++) {
      if (copyData[i].key === key) {
        const copyDataItem = copyData[i];
        copyDataItem.product_sku_name = value;
        copyData[i] = copyDataItem;
      }
    }
    updateState({ data: copyData });
  };

  const createData = ({
    tenantSku, dataItem, sellerOffer, sellerOffers, otherDepStocksData, selectedTenant, autoPrintDescription, cfPurchaseIndentLine,
  }) => {
    const dataFromSelectedTenant = otherDepStocksData?.tenant_products?.filter((allTenantId) => allTenantId?.tenant_id === selectedTenant);

    const copyDataItem = JSON.parse(JSON.stringify(dataItem));
    copyDataItem.tenant_product_id = tenantSku?.tenant_product_id;
    copyDataItem.otherDepartmentStock = tenantSku?.department_quantities;
    copyDataItem.product_sku_id = tenantSku?.product_info?.product_sku_id;
    copyDataItem.product_sku_name = `${tenantSku?.product_info.product_sku_name}`;
    copyDataItem.internal_sku_code = tenantSku?.product_info?.internal_sku_code;
    copyDataItem.ref_product_code = tenantSku?.product_info?.ref_product_code;
    copyDataItem.asset1 = tenantSku?.product_info?.assets?.[0]?.url || '';
    copyDataItem.purchase_uom_info = tenantSku?.purchase_uom_info || '';
    copyDataItem.sku = tenantSku?.product_info?.product_sku_id || '';
    copyDataItem.tax = JSON.stringify({ tax_id: tenantSku?.product_info?.tax_id, tax_name: tenantSku?.product_info?.tax_name, tax_value: tenantSku?.product_info?.tax_value });
    copyDataItem.tax_id = tenantSku?.product_info?.tax_id || 0;
    copyDataItem.uom_id = tenantSku?.purchase_uom_info?.uom_id || 0;
    copyDataItem.uomId = tenantSku?.purchase_uom_info?.uom_id || 0;
    copyDataItem.productCategoryInfo = tenantSku?.product_category_info || {};
    copyDataItem.uom_info = tenantSku?.purchase_uom_info;
    copyDataItem.uom_list = tenantSku?.uom_list;
    copyDataItem.uomGroup = tenantSku?.group_id;
    copyDataItem.quantity = 1;
    copyDataItem.pos_quantity_against_product = dataFromSelectedTenant?.[0]?.pos_quantity_against_product;
    copyDataItem.targetPrice = sellerOffers?.sort((a, b) => b.offer_price - a.offer_price)?.[0]?.offer_price;
    copyDataItem.maxPrice = sellerOffers?.sort((a, b) => b.offer_price - a.offer_price)?.[0]?.offer_price;
    copyDataItem.minPrice = sellerOffers?.sort((a, b) => a.offer_price - b.offer_price)?.[0]?.offer_price;
    copyDataItem.avaragePrice = Helpers.getValueTotalInObject(sellerOffers, 'offer_price') / sellerOffers?.length;
    copyDataItem.remark = autoPrintDescription ? (otherDepStocksData?.description || '')?.replace(/<[^>]+>/g, '') || otherDepStocksData?.product_info?.description : '';
    copyDataItem.remarkRequired = !!otherDepStocksData?.description?.replace(/<[^>]+>/g, '');
    copyDataItem.lineCustomFields = FormHelpers.lineSystemFieldValue(cfPurchaseIndentLine, [...(tenantSku?.custom_fields ?? []), ...(otherDepStocksData?.system_custom_fields ?? [])])?.map((i) => ({
      ...i,
      fieldValue: i?.fieldName === 'Rate' ? Number(sellerOffer?.offer_price || 0) : (i?.fieldName === 'Quantity' ? Number(sellerOffer?.moq || 1) : i?.fieldValue),
    }));
    copyDataItem.plan_number = '';
    copyDataItem.req_by_date = toISTDate(new Date(), 'DD/MM/YYYY');
    return copyDataItem;
  }

  const handleProductChange = (tenantSku, key, otherDepStocksData, isMultiMode, callback) => {

    const autoPrintDescription = user?.tenant_info?.global_config?.settings?.print_description_automatically;
    if (isMultiMode && Array.isArray(tenantSku)) {
      const tenantProductIds = tenantSku?.map((item) => item?.tenant_product_id)?.join(',');
      getTenantSkuOffer('',
        '',
        (sellerOffers) => {
          const copyData = [];
          let tenantProductSellerOffer;
          for (let i = 0; i < tenantSku?.length; i++) {
            const tenantProductId = tenantSku[i]?.tenant_product_id;
            tenantProductSellerOffer = sellerOffers[tenantProductId]?.[0];
            const otherDepStocksDataItem = otherDepStocksData?.find((item) => item?.product_sku_id === tenantSku[i]?.product_sku_id);
            const newDataRow = createData({
              tenantSku: tenantSku[i],
              dataItem: {
                key: uuidv4(),
                lineCustomFields: cfPurchaseIndentLine,
              },
              sellerOffer: tenantProductSellerOffer,
              sellerOffers: sellerOffers[tenantProductId],
              otherDepStocksData: otherDepStocksDataItem,
              selectedTenant,
              autoPrintDescription,
              cfPurchaseIndentLine,
            });
            copyData.push(newDataRow);
          }
          if (callback) callback();

          updateState({
            data: [...data, ...copyData],
          });
        },
        tenantProductIds);
    } else {
      getTenantSkuOffer(tenantSku?.tenant_product_id, '', (sellerOffer, sellerOffers) => {
        const copyData = JSON.parse(JSON.stringify(data));
        for (let i = 0; i < copyData.length; i++) {
          if (copyData[i].key === key) {
            copyData[i] = createData({
              tenantSku,
              dataItem: copyData[i],
              sellerOffer,
              sellerOffers,
              otherDepStocksData,
              selectedTenant,
              autoPrintDescription,
              cfPurchaseIndentLine,
            });
          }
        }
        updateState({ data: copyData });
      });
    }
  }

  const handleMultiProductChange = (selectedProductData, key, otherDepStocksData, callback) => {
    handleProductChange(selectedProductData, key, otherDepStocksData, true, callback);
  };

  const customLineInputChange = (fieldValue, key) => {

    const copyData = JSON.parse(JSON.stringify(data));
    for (let i = 0; i < copyData.length; i++) {
      if (copyData[i]?.key === key) {
        copyData[i].lineCustomFields = fieldValue;
        const qtyCf = fieldValue?.find((j) => j?.fieldName === 'Quantity');
        copyData[i].quantity = qtyCf?.fieldValue > 0 ? parseFloat(Number(qtyCf?.fieldValue).toFixed(DEFAULT_CUR_ROUND_OFF)) : '';
      }
    }
    updateState({ data: copyData });
  };

  const handleDelete = (key) => {

    if (data.length === 1) {
      updateState({
        data: [{
          key: uuidv4(),
          asset1: '',
          product_sku_name: '',
          quantity: '',
          uom_id: null,
          req_by_date: undefined,
          plan_number: '',
          lineCustomFields: cfPurchaseIndentLine || [],
        }],
      });
    } else {
      const copyData = data.filter((item) => item.key !== key);
      updateState({ data: copyData });
    }
  };

  const addNewRow = () => {

    const copyData = JSON.parse(JSON.stringify(data));
    copyData.push({
      key: uuidv4(),
      asset1: '',
      product_sku_name: '',
      quantity: '',
      uom_id: null,
      req_by_date: undefined,
      plan_number: '',
      lineCustomFields: cfPurchaseIndentLine || [],
    });
    updateState({ data: copyData });
  };

  const getPurchaseIndentErrors = () => {

    const {
      docLevelError,
      lineLevelError,
    } = purchaseIndentErrors({
      piNumber,
      selectedTenant,
      selectedDepartment,
      selectedTenantDepartment,
      cfPurchaseIndentDoc,
      data,
      cfPurchaseIndentLine,
    });

    return {
      docLevelErrors: docLevelError ?? [],
      lineLevelErrors: lineLevelError ?? [],
    };
  };

  const createPI = (withApproval) => {

    const { docLevelErrors, lineLevelErrors } = getPurchaseIndentErrors();

    updateState({ formSubmitted: true, data: JSON.parse(JSON.stringify(data)) });

    if (docLevelErrors?.length === 0 && lineLevelErrors?.length === 0) {

      let purchaseIndentLines = [];
      if (formCase !== 'UPDATE') {

        for (let i = 0; i < data.length; i++) {

          const piLine = {
            tenant_product_id: data?.[i].tenant_product_id,
            product_sku_name: data?.[i].product_sku_name,
            remarks: data?.[i]?.remark,
            uom_id: data?.[i]?.uom_info?.uom_id,
            uom_info: [data?.[i]?.uom_info],
            quantity: Number(data?.[i]?.quantity),
            product_sku_id: data?.[i].product_sku_id,
            required_by_date: data?.[i]?.req_by_date,
            planning_number: data?.[i]?.plan_number,
            custom_fields: CustomFieldHelpers.postCfStructure(data[i]?.lineCustomFields),
            pr_line_id: data?.[i]?.pr_line_id,
            mo_line_id: data?.[i]?.mo_line_id,
          };
          purchaseIndentLines?.push(piLine);
        }

        const payload = {
          pr_id: prId,
          mo_id: moId,
          mrp_id: mrpId,
          pi_number: initialPiNumber?.toLowerCase()?.trim() === piNumber?.toLowerCase()?.trim() ? null : piNumber,
          seq_id: docSeqId,
          tenant_id: selectedTenant,
          purchase_indent_lines: purchaseIndentLines,
          remarks: remark,
          status: withApproval ? 'ISSUED' : 'DRAFT',
          attachments: fileList?.map((attachment) => ({
            url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
          })) || [],
          tenant_department_id: selectedTenantDepartment,
          pi_date: date,
          custom_fields: CustomFieldHelpers.postCfStructure(cfPurchaseIndentDoc),
        };

        createPurchaseIndent({ body: [payload] }, (pi) => {
          const createdPI = pi?.data?.[0];
          if (selectedTags?.length) {
            const tagPayload = {
              entity_name: 'PURCHASE_INDENT',
              entity_id: createdPI?.pi_id,
              action: 'ADD',
              tag: selectedTags.join(','),
            };

            createTag(tagPayload, () => {
              updateState({ currentAction: false });
              history.push(`/purchase/purchase-indent/view/${createdPI?.pi_id}`);
            });
          } else {
            updateState({ currentAction: false });
            history.push(`/purchase/purchase-indent/view/${createdPI?.pi_id}`);
          }
        });
      } else {
        for (let i = 0; i < data.length; i++) {

          const piLine = {
            pi_line_id: data?.[i]?.pi_line_id,
            tenant_product_id: data?.[i].tenant_product_id,
            product_sku_name: data?.[i].product_sku_name,
            quantity: Number(data?.[i]?.quantity),
            uom_id: data?.[i]?.uom_id || null,
            uom_info: [data?.[i]?.uom_info],
            remarks: data?.[i]?.remark,
            custom_fields: CustomFieldHelpers.postCfStructure(data[i]?.lineCustomFields),
            required_by_date: data?.[i]?.req_by_date,
            planning_number: data?.[i]?.plan_number,
          }
          purchaseIndentLines?.push(piLine);
        }

        const payload = {
          pi_id: piId,
          tenant_id: selectedTenant,
          tenant_department_id: selectedTenantDepartment,
          remarks: remark,
          status: withApproval ? 'ISSUED' : 'DRAFT',
          attachments: fileList?.map((attachment) => ({
            url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
          })) || [],
          custom_fields: CustomFieldHelpers.postCfStructure(cfPurchaseIndentDoc),
          update_document_reason: updateDocumentReason,
          pi_number: initialPiNumber?.toLowerCase()?.trim() === piNumber?.toLowerCase()?.trim() ? null : piNumber,
          pi_date: date,
          purchase_indent_lines: purchaseIndentLines,
        };
        updatePurchaseIndent({ body: payload }, (pi) => {
          const updatedPI = pi?.data;
          if (selectedTags?.length) {

            const tagPayload = {
              entity_name: 'PURCHASE_INDENT',
              entity_id: piId,
              action: 'REPLACE',
              tag: selectedTags?.join(','),
            };
            createTag(tagPayload, () => {
              updateState({ currentAction: false });
              history.push(`/purchase/purchase-indent/view/${updatedPI?.pi_id}`);
            });
          } else {
            updateState({ currentAction: false });
            history.push(`/purchase/purchase-indent/view/${updatedPI?.pi_id}`);
          }
        })
      }
    }
  };

  const fixedMenuBar = user?.side_menu_bar_type === 'FIXED';
  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  const { docLevelErrors, lineLevelErrors } = getPurchaseIndentErrors();

  return (
    <Fragment>
      {getPILoading ? (
        <ViewLoadingSkull fixedMenuBar={fixedMenuBar} />
      ) : (
        <Fragment>
          <div className='form__wrapper' style={{ paddingTop: !!selectedPR || !!selectedMO || !!selectedMRP ? '0px' : '90px' }}>

            {(formSubmitted && (docLevelErrors?.length > 0 || lineLevelErrors?.length > 0)) && (
              <ErrorHandle message="Mandatory fields required" docLevelErrors={docLevelErrors} lineLevelErrors={lineLevelErrors} />
            )}

            <div className='ant-row'>
              <div className='ant-col-md-24'>

                <div className='form__section'>

                  <div className='flex-display flex-align-c mg-bottom-5 pd-right-15'>
                    <H3Text text="PART A" className="form__section-title" />
                    <div className="form__section-line" />
                    <div style={{
                      display: 'flex', justifyContent: 'end', alignItems: 'center', marginLeft: '10px',
                    }}
                    >
                      <CustomDocumentInputs
                        customFields={cfPurchaseIndentDoc}
                        updateCustomFields={(cf) => updateState({ cfPurchaseIndentDoc: cf })}
                      />
                    </div>
                  </div>

                  <div className='form__section-inputs mg-bottom-20'>
                    <div className='ant-row'>
                      <DocumentNumberSeqInput
                        valueFromProps={piNumber}
                        updateCase={match?.params?.piId}
                        setInitialDocSeqNumber={(value) => updateState({ initialPiNumber: value })}
                        entityName="PURCHASE_INDENT"
                        docSeqId={docSeqId}
                        tenantId={selectedTenant}
                        onChangeFromProps={(event, newValue, seqId) => {
                          updateState({
                            piNumber: newValue ? (newValue || '') : (event?.target?.value || ''),
                            docSeqId: seqId,
                          })
                        }}
                        docTitle="Purchase Indent #"
                        formSubmitted={formSubmitted}
                      />

                      <div className='ant-col-md-6'>
                        <div className='form__input-row'>
                          <H3Text
                            required
                            text='Location'
                            className="form__input-row__label"
                          />
                          <div className='form__input-row__input'>
                            <TenantSelector
                              hideTitle
                              selectedTenant={selectedTenant}
                              showSearch={false}
                              onChange={(value) => {
                                updateState({
                                  selectedTenant: value,
                                  selectedDepartment: user?.user_tenants?.find((item) => item?.tenant_id === value)?.tenant_department_info?.department_id,
                                  selectedTenantDepartment: user?.user_tenants?.find((item) => item?.tenant_id === value)?.tenant_department_info?.tenant_department_id,
                                });
                              }}
                              disabled={createPILoading || mrpToPiData || selectedMO || selectedPR || match?.params?.piId}
                              placeholder="Select Business Unit"
                              labelClassName="form__input-row__label"
                              inputClassName="orgFormInput input"
                              includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_INDENT, Helpers.permissionTypes.CREATE)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className='ant-col-md-6'>
                        <div className='form__input-row'>
                          <H3Text
                            required
                            text="Department"
                            className="form__input-row__label"
                          />
                          <div className='form__input-row__input'>
                            <SelectDepartment
                              hideTitle
                              selectedDepartment={selectedDepartment}
                              onChange={(value) => updateState({
                                selectedDepartment: value?.department_id,
                                selectedTenantDepartment: value?.tenant_department_id,
                              })}
                              tenantId={selectedTenant}
                              disabled={createPILoading}
                              labelClassName="form__input-row__label"
                              inputClassName="orgFormInput input"
                              emptyNotAllowed
                            />
                          </div>
                        </div>
                      </div>

                      <div className='ant-col-md-6'>
                        <div className='form__input-row orgInputContainer'>
                          <H3Text
                            text="Purchase Indent Date"
                            className="form__input-row__label"
                          />
                          <div className="form__input-row__input">
                            <DatePicker
                              value={date}
                              onChange={(value) => {
                                updateState({
                                  date: value,
                                });
                              }}
                              style={{
                                borderColor: formSubmitted && !date ? 'red' : 'rgba(68, 130, 218, 0.2)',
                                border: '1px solid rgba(68, 130, 218, 0.2)',
                                borderRadius: '2px',
                                height: '28px',
                                padding: '1px 3px',
                                width: '100%',
                                background: 'white',
                              }}
                              format="DD/MM/YYYY"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="ant-col-md-6">
                        <div className="form__input-row ">
                          <div className="form__input-row__label">
                            Labels
                          </div>
                          <TagSelector
                            hideTitle
                            entityType="PURCHASE_INDENT"
                            selectedTags={selectedTags}
                            isMultiple
                            showSearch
                            onChange={(value) => {
                              updateState({ selectedTags: value });
                            }}
                            placeholder="Select Tags"
                            isForm
                            containerWrapper="form__input-row__input"
                            disabled={createPILoading}
                            maxTagCount="responsive"
                          />
                        </div>
                      </div>

                      <CustomFieldV3
                        customFields={cfPurchaseIndentDoc}
                        formSubmitted={formSubmitted}
                        customInputChange={(value, cfId) => customInputChange(value, cfId)}
                        wrapperClassName="ant-col-md-6"
                        containerClassName="form__input-row"
                        labelClassName="form__input-row__label"
                        inputClassName="form__input-row__input"
                        errorClassName="form__input-row__input-error"
                        disableCase={createPILoading || updatePILoading || getPILoading}
                        hideTitle
                      // isCarryForward={mrpToPrData}
                      />
                    </div>
                  </div>
                </div>

                <div className='form__section'>

                  <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                    <H3Text text="PART B" className="form__section-title " />
                    <div className="form__section-line" />
                  </div>

                  <div className='ant-row'>

                    <div className="form__input-row">
                      <H3Text
                        text="Internal Remarks"
                        className="form__input-row__label"
                      />
                      <div className="form__input-row__input">
                        <textarea
                          rows="5"
                          cols="50"
                          onChange={(event) => {
                            updateState({ remark: event.target.value });
                          }}
                          value={remark}
                          style={{
                            resize: 'none',
                            backgroundColor: 'rgb(239, 239, 239)',
                            border: 'none',
                            borderRadius: '4px',
                            marginRight: '15px',
                            height: '103px',
                            padding: '4px 8px',
                          }}
                        />
                      </div>
                    </div>

                    <div className="ant-col-md-12">
                      <div className="form__data-attachment">
                        <label className="form__input-row__label">Attachment(s)</label>
                        <Upload
                          action={Constants.UPLOAD_FILE}
                          listType="picture-card"
                          fileList={fileList}
                          // disabled={getSOByIdLoading}
                          multiple
                          onChange={(fileListData) => {
                            updateState({
                              fileList: fileListData?.fileList?.map((item) => ({
                                ...item,
                                url: item?.response?.response?.location,
                              })),
                            });
                          }}
                        >
                          {fileList?.length >= 20 ? null : uploadButton}
                        </Upload>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='form__lines-wrapper'>
              <PurchaseIndentLines
                handleProductChangeValue={() => handleProductChangeValue}
                handleProductChange={handleProductChange}
                updateData={(updatedData) => updateState({ data: updatedData })}
                selectedTenant={selectedTenant}
                selectedTenantDepartment={selectedTenantDepartment}
                handleMultiProductChange={handleMultiProductChange}
                formSubmitted={formSubmitted}
                customLineInputChange={(fieldValue, key) => customLineInputChange(fieldValue, key)}
                data={data}
                handleDelete={handleDelete}
                cfPurchaseIndentLine={cfPurchaseIndentLine}
                visibleColumns={visibleColumns}
                setVisibleColumns={(value) => {
                  updateState({ visibleColumns: value });
                }}
                isCarryForward={true}
                selectedMO={selectedMO}
              />
            </div>

            <div className="form__data-wrapper">
              <div className="ant-row">
                <div className="ant-col-md-24">
                  <div
                    className={`new-row-button ${data?.find((item) => !(item?.product_sku_id || item?.product_sku_info?.product_sku_id)) ? 'new-row-button__disabled' : ''}`}
                    onClick={() => {
                      if (!data?.find((item) => !(item?.product_sku_id || item?.product_sku_info?.product_sku_id))) {
                        addNewRow();
                      }
                    }}
                  >
                    <span className="new-row-button__icon">
                      <PlusCircleFilled />
                    </span>
                    <div>New Item</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
      <div className='form__footer'>

        {(formCase === 'UPDATE') ? (
          <Fragment>
            <PRZConfirmationPopover
              title="Are you sure you want to update?"
              content={(
                <Fragment>
                  <PRZText text="Reason" required />
                  <PRZInput
                    placeholder="Enter update reason"
                    value={updateDocumentReason}
                    onChange={(e) => updateState({ updateDocumentReason: e.target.value })}
                  />
                </Fragment>
              )}
              onConfirm={() => {
                updateState({ currentAction: 'SAVE_AS_DRAFT' });
                if (!createPILoading) {
                  createPI(false);
                }
              }}
              confirmButtonText="Confirm"
              cancelButtonText="Back"
              confirmDisabled={!updateDocumentReason}
            >
              <PRZButton
                type="default"
                wrapperStyle={{ marginRight: '10px' }}
                buttonStyle={{
                  width: '130px',
                  height: '40px',
                  border: '1px solid #2d7df7',
                  color: '#2d7df7',
                }}
                isLoading={(createPILoading || updatePILoading) && currentAction === 'SAVE_AS_DRAFT'}
                disabled={createPILoading || updatePILoading}
              >
                Save as Draft
              </PRZButton>
            </PRZConfirmationPopover>
            <PRZConfirmationPopover
              title="Are you sure you want to update?"
              content={(
                <Fragment>
                  <PRZText text="Reason" required />
                  <PRZInput
                    placeholder="Enter update reason"
                    value={updateDocumentReason}
                    onChange={(e) => updateState({ updateDocumentReason: e.target.value })}
                  />
                </Fragment>
              )}
              onConfirm={() => {
                updateState({ currentAction: 'SAVE_AND_ISSUE' });
                if (!createPILoading) {
                  createPI(true);
                }
              }}
              confirmButtonText="Confirm"
              cancelButtonText="Back"
              confirmDisabled={!updateDocumentReason}
            >
              <PRZButton
                isLoading={(createPILoading || updatePILoading) && currentAction === 'SAVE_AND_ISSUE'}
                disabled={createPILoading || updatePILoading}
                buttonStyle={{ width: '130px', height: '40px' }}
              >
                Save and Issue
              </PRZButton>
            </PRZConfirmationPopover>
          </Fragment>
        ) : (
          <Fragment>
            <PRZButton
              type="default"
              onClick={() => {
                updateState({ currentAction: 'SAVE_AS_DRAFT' });
                if (!createPILoading) {
                  createPI(false);
                }
              }}
              isLoading={(createPILoading || updatePILoading) && currentAction === 'SAVE_AS_DRAFT'}
              disabled={createPILoading || updatePILoading}
              wrapperStyle={{ marginRight: '10px' }}
              buttonStyle={{
                width: '130px',
                height: '40px',
                border: '1px solid #2d7df7',
                color: '#2d7df7',
              }}
            >
              Save as Draft
            </PRZButton>
            <PRZButton
              onClick={() => {
                updateState({ currentAction: 'SAVE_AND_ISSUE' });
                if (!createPILoading) {
                  createPI(true);
                }
              }}
              isLoading={(createPILoading || updatePILoading) && currentAction === 'SAVE_AND_ISSUE'}
              disabled={createPILoading || updatePILoading}
              buttonStyle={{ width: '130px', height: '40px' }}
            >
              Save and Issue
            </PRZButton>
          </Fragment>
        )}
        {(isDataMaskingPolicyEnable && isHideCostPrice) && <RestrictedAccessMessage message="You don't have access to view & edit target price" />}
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, ProductReducers, CFV2Reducers, CreatePurchaseIndent, UpdatePurchaseIndent, GetPurchaseIndent,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  products: ProductReducers.products,
  getDocCFV2Loading: CFV2Reducers.getDocCFV2Loading,
  cfV2DocPurchaseIndent: CFV2Reducers.cfV2DocPurchaseIndent,
  priceMasking: UserReducers.priceMasking,
  createPILoading: CreatePurchaseIndent.loading,
  updatePILoading: UpdatePurchaseIndent.loading,
  purchaseIndent: GetPurchaseIndent.data,
  getPILoading: GetPurchaseIndent.loading,
  selectedPurchaseIndent: GetPurchaseIndent.data?.[0],
});

const mapDispatchToProps = (dispatch) => ({
  createPurchaseIndent: (payload, callback) => dispatch(CreatePurchaseIndent.actions.request(payload, callback)),
  updatePurchaseIndent: (payload, callback) => dispatch(UpdatePurchaseIndent.actions.request(payload, callback)),
  getPurchaseIndent: (payload, callback) => dispatch(GetPurchaseIndent.actions.request(payload, callback)),
  getPurchaseIndentSuccess: (selectedPurchaseIndent) => dispatch(GetPurchaseIndent.actions.success(selectedPurchaseIndent)),
  getDocCFV2: (payload, callback) => dispatch(CFV2Actions.getDocCFV2(payload, callback)),
  getDocCFV2Success: (customFields) => dispatch(CFV2Actions.getDocCFV2Success(customFields)),
  getTenantSkuOffer: (tenantProductId, tenantSellerId, callback, tenantProductIds) => dispatch(OfferActions.getOfferByTenantSku(tenantProductId, tenantSellerId, callback, tenantProductIds)),
  createTag: (payload, callback) => dispatch(TagActions.createTag(payload, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PurchaseIndentForm));